import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import isMobile from "ismobilejs";
import {
    getProductIdsForCompare,
    getProductsForCompare,
} from "@insite/client-framework/Store/Components/CompareProductsDrawer/CompareProductsDrawerSelectors";
import loadProductIdsToCompare from "@insite/client-framework/Store/Components/CompareProductsDrawer/Handlers/LoadProductIdsToCompare";
import loadProductsToCompare from "@insite/client-framework/Store/Components/CompareProductsDrawer/Handlers/LoadProductsToCompare";
import removeAllProductsFromCompare from "@insite/client-framework/Store/Components/CompareProductsDrawer/Handlers/RemoveAllProductsFromCompare";
import removeProductIdToCompare from "@insite/client-framework/Store/Components/CompareProductsDrawer/Handlers/RemoveProductFromCompare";
import setProductCompareReturnUrl from "@insite/client-framework/Store/Components/CompareProductsDrawer/Handlers/SetProductCompareReturnUrl";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import { CarouselSlide, CarouselStyles } from "@insite/content-library/Components/Carousel";
import ProductCarouselProductCondensed from "@insite/content-library/Components/ProductCarouselProductCondensed";
import Button, { ButtonIcon, ButtonIconPresentationProps, ButtonPresentationProps } from "@insite/mobius/Button";
import { ClickableProps } from "@insite/mobius/Clickable";
import Drawer, { DrawerPresentationProps } from "@insite/mobius/Drawer";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps, GridOffset } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import ChevronDown from "@insite/mobius/Icons/ChevronDown";
import ChevronsUpDown from "@insite/mobius/Icons/ChevronsUpDown";
import X from "@insite/mobius/Icons/X";
import Img from "@insite/mobius/Img/Img";
import { Clickable, Hidden } from "@insite/mobius/index";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries, getProp } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import getContrastColor from "@insite/mobius/utilities/getContrastColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import CrossIcon from "@NA/Icons/CrossIcon";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";
import ADIColors from "@NA/Utils/ADIColors";

const PRODUCT_COMPARE_CAROUSEL_ID = "product-compare-carousel-id";

interface OwnProps {
    extendedStyles?: ProductCompareFlyOutStyles;
}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

const mapStateToProps = (state: ApplicationState) => ({
    productIds: getProductIdsForCompare(state),
    products: getProductsForCompare(state),
    productComparePageLink: getPageLinkByPageType(state, "ProductComparePage"),
    currentLocation: getLocation(state),
});

const mapDispatchToProps = {
    loadProductIdsToCompare,
    loadProductsToCompare,
    removeProductIdToCompare,
    removeAllProductsFromCompare,
    setProductCompareReturnUrl,
};

export interface ProductCompareFlyOutStyles {
    carousel?: CarouselStyles;
    carouselDrawer?: DrawerPresentationProps;
    carouselDrawerContainer?: InjectableCss;
    removeProductFromCompareButton?: ButtonPresentationProps;
    removeProductFromCompareButtonIcon?: ButtonIconPresentationProps;
    headerGridContainer?: GridContainerProps;
    headerTitleGridItem?: GridItemProps;
    headerTitleText?: TypographyPresentationProps;
    headerActionsGridItem?: GridItemProps;
    headerRemoveAllButton?: ButtonPresentationProps;
    headerCompareButton?: ButtonPresentationProps;
    compareProductsContainer?: GridContainerProps;
    productItems?: GridItemProps;
    compareButtonContainer?: GridItemProps;
    productsBox?: GridItemProps;
    productsBoxContainer: GridItemProps;
    productContainer?: InjectableCss;
    productImage?: InjectableCss;
    productContent?: InjectableCss;
    closeIcon?: InjectableCss;
    productBrand?: ClickableProps;
    productTitle?: ClickableProps;
    showHideText?: TypographyProps;
}

export const productCompareFlyOutStyles: ProductCompareFlyOutStyles = {
    carouselDrawer: {
        position: "bottom",
        size: 0,
        cssOverrides: {
            scrim: css`
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            css`
                                height: 125px;
                            `,
                        ],
                        "max",
                    )}
            `,
            drawerBody: css`
                height: auto;
                width: 100%;
                box-shadow: 0 -4px 6px 0 rgba(0, 0, 0, 0.18);
                background-color: rgba(0, 40, 86, 0.6);
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            css`
                                height: 125px;
                            `,
                        ],
                        "max",
                    )}
            `,
            drawerTitle: css`
                background: ${getColor("common.accent")};
                color: ${getContrastColor("common.accent")};
                padding: 0;
                min-height: 43px;
                background-image: linear-gradient(268deg, ${ADIColors.darkBlue2} 3%, ${ADIColors.darkBlue3} 100%);
            `,
        },
        closeButtonProps: {
            css: css`
                display: none;
            `,
        },
        enableClickThrough: true,
    },
    carouselDrawerContainer: {
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            display: none;
                        `,
                    ],
                    "max",
                )}
        `,
    },
    carousel: {
        titleText: {
            css: css`
                display: none;
            `,
        },
    },
    removeProductFromCompareButton: {
        buttonType: "solid",
        color: "common.accent",
        sizeVariant: "small",
    },
    headerGridContainer: {
        gap: 0,
        css: css`
            max-width: 1474px;
            margin: 0 auto;
            padding: 0 17px;
            min-height: 43px;
            width: 100%;
            display: flex;
            align-items: center;
        `,
    },
    headerTitleGridItem: {
        width: [12, 6, 6, 6, 6],
        align: "middle",
    },
    headerTitleText: {
        variant: "h4",
        css: css`
            margin-bottom: 0;
            color: ${ADIColors.white};
            font-size: ${getProp("theme.typography.headerSecondary.size")};
        `,
    },
    headerActionsGridItem: {
        width: [12, 6, 6, 6, 6],
        css: css`
            justify-content: flex-end;
            align-items: center;
        `,
    },
    headerRemoveAllButton: {
        css: css`
            margin: 5px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            flex: 1;
                        `,
                    ],
                    "max",
                )}
        `,
        variant: "secondary",
    },
    headerCompareButton: {
        css: css`
            margin: 5px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            flex: 1;
                        `,
                    ],
                    "max",
                )}
            :hover {
                background-color: ${getColor("primary")} !important;
                border-color: transparent;
                opacity: 0.9;
            }
        `,
    },
    compareProductsContainer: {
        css: css`
            margin: 0 auto;
            ${GridOffset} {
                width: 100% !important;
                margin: 0;
                padding: 0 17px;
            }
        `,
    },
    productItems: {
        width: 10,
        css: css`
            padding: 0;
            gap: 43px;
        `,
    },
    compareButtonContainer: {
        width: 2,
        css: css`
            padding: 0;
            align-items: center;
            justify-content: flex-end;
            button {
                color: ${ADIColors.white};
                font-size: ${getProp("theme.typography.headerSecondary.size")};
                font-weight: 800;
                min-width: 180px;
                min-height: 40px;
                border-radius: 7px;
                box-shadow: inset 0 -4px 0 0 ${getColor("secondary")};
                background-color: ${getColor("primary")};
                padding: 10px 5px;
                text-transform: uppercase;
            }
        `,
    },
    productsBox: {
        width: 4,
        css: css`
            padding: 0;
            border: 2px dashed ${ADIColors.white};
            height: 86px;
            margin: 20px 0 !important;
            overflow: hidden;
        `,
    },
    productsBoxContainer: {
        width: 4,
        css: css`
            padding: 0;
            border: 2px solid ${ADIColors.white};
            height: 86px;
            margin: 20px 0 !important;
            overflow: hidden;
        `,
    },
    productContainer: {
        css: css`
            width: 100%;
            background-color: ${ADIColors.white};
            display: flex;
            height: 100%;
            position: relative;
        `,
    },
    productImage: {
        css: css`
            img {
                margin: 11px 18px 6px 20px;
                width: 66px;
                height: 66px;
                text-align: center;
            }
        `,
    },
    productContent: {
        css: css`
            padding: 12px 5px 12px 0;
            position: initial;
        `,
    },
    closeIcon: {
        css: css`
            position: absolute;
            top: 2px;
            right: 7px;
            z-index: 9;
            cursor: pointer;
            svg {
                width: 12px;
                height: 11px;
            }
        `,
    },
    productBrand: {
        css: css`
            font-size: 12px;
            font-weight: 600;
            color: ${ADIColors.gray20};
            text-decoration: underline;
        `,
    },
    productTitle: {
        css: css`
            font-size: ${getProp("theme.typography.headerSecondary.size")};
            font-weight: 600;
            font-stretch: normal;
            font-style: normal;
            line-height: 1.21;
            letter-spacing: normal;
            color: ${ADIColors.darkBlue1};
            padding-top: 5px;
            overflow: hidden;
            word-break: break-word;

            display: -webkit-box;
            line-clamp: 2;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
            :hover {
                text-decoration: underline;
            }
        `,
    },
    showHideText: {
        css: css`
            font-size: ${getProp("theme.typography.h6.size")};
            font-weight: bold;
            text-transform: uppercase;
            color: ${ADIColors.white};
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
            > span {
                display: flex;
                margin: 0 4px;
            }
        `,
    },
};

const mapProductsToSlides = (
    products: ProductModel[],
    styles: ProductCompareFlyOutStyles,
    removeProductIdToCompare: (productId: string) => void,
) =>
    products.map(product => ({
        id: product?.id,
        renderComponent: function ProductCarouselProductCondensedRenderer() {
            return (
                <ProductCarouselProductCondensed
                    carouselId={PRODUCT_COMPARE_CAROUSEL_ID}
                    product={product}
                    labelsVisible={false}
                    showImage={true}
                    showBrand={false}
                    showTitle={true}
                    showPartNumbers={true}
                    showPrice={true}
                    showAddToCart={false}
                    showAddToList={false}
                    condensedActionsTemplate={(product: ProductModel) => {
                        return (
                            <Button
                                {...styles.removeProductFromCompareButton}
                                onClick={() => removeProductIdToCompare(product?.id)}
                                data-test-selector="productCompareRemoveProductButton"
                                data-test-key={product?.id}
                            >
                                <VisuallyHidden>
                                    {translate("Remove Product {0} from Compare", product?.productTitle)}
                                </VisuallyHidden>
                                <ButtonIcon {...styles.removeProductFromCompareButtonIcon} src={X} />
                            </Button>
                        );
                    }}
                />
            );
        },
    }));

const ProductCompareFlyOut = ({
    extendedStyles,
    history,
    productIds,
    products,
    productComparePageLink,
    currentLocation,
    loadProductIdsToCompare,
    loadProductsToCompare,
    removeProductIdToCompare,
    removeAllProductsFromCompare,
    setProductCompareReturnUrl,
}: Props) => {
    const [styles] = useState(() => mergeToNew(productCompareFlyOutStyles, extendedStyles));
    const [slides, setSlides] = useState<CarouselSlide[]>([]);
    const [openCompare, setOpenCompare] = useState<string | null>(null);

    useEffect(() => {
        // We load in the ProductIds to Compare from storage
        loadProductIdsToCompare();
        const showCompareTray = window?.localStorage.getItem("showCompareTray");
        setOpenCompare(showCompareTray);
    }, []);

    useEffect(() => {
        // Load in products from the ProductCompareService Compare Storage
        if (productIds.length === 0) {
            return;
        }

        loadProductsToCompare({ id: PRODUCT_COMPARE_CAROUSEL_ID, productIds });
    }, [productIds]);

    useEffect(() => {
        if (products.length === 0) {
            return;
        }
        setSlides(mapProductsToSlides(products, styles, productId => removeProductIdToCompare({ productId })));
    }, [products, styles]);

    const handleRemoveAll = () => {
        removeAllProductsFromCompare();
    };
    const handleOpenCompare = () => {
        if (productComparePageLink) {
            setProductCompareReturnUrl({ returnUrl: currentLocation.pathname + currentLocation.search });
            history.push(productComparePageLink.url);
        }
    };

    const toggleCompare = (value: string) => {
        setOpenCompare(value);
        window.localStorage.setItem("showCompareTray", value);
    };

    if (products.length === 0 || isMobile().any) {
        return null;
    }

    const renderCompareProduct = (index: number) => {
        const productId = productIds[index];
        const product = productId ? products.find(p => p.id === productId) : undefined;
        return (
            <GridItem {...(product ? styles.productsBoxContainer : styles.productsBox)}>
                {product && (
                    <StyledWrapper {...styles.productContainer}>
                        <StyledWrapper {...styles.productImage}>
                            <Clickable href={`/Product/${product?.urlSegment}`}>
                                <Img src={product?.smallImagePath} />
                            </Clickable>
                        </StyledWrapper>
                        <StyledWrapper {...styles.productContent}>
                            <Typography>
                                <Clickable {...styles.productBrand} href={`/Catalog${product?.brand?.detailPagePath}`}>
                                    {product?.brand?.name}
                                </Clickable>
                            </Typography>
                            <Typography>
                                <Clickable {...styles.productTitle} href={`/Product/${product?.urlSegment}`}>
                                    {product?.productTitle}
                                </Clickable>
                            </Typography>
                            <StyledWrapper
                                {...styles.closeIcon}
                                onClick={() => {
                                    removeProductIdToCompare({
                                        productId: product?.id,
                                    });
                                }}
                            >
                                <CrossIcon />
                            </StyledWrapper>
                        </StyledWrapper>
                    </StyledWrapper>
                )}
            </GridItem>
        );
    };

    return (
        <Drawer
            isOpen={true}
            headline={
                <ProductCompareHeader
                    styles={styles}
                    productCount={products.length}
                    onOpenCompare={handleOpenCompare}
                    onRemoveAll={handleRemoveAll}
                    toggleCompareFn={toggleCompare}
                    openCompare={openCompare}
                />
            }
            {...styles.carouselDrawer}
            data-test-selector="productCompareDrawer"
        >
            {openCompare === "true" ? (
                <StyledWrapper {...styles.carouselDrawerContainer}>
                    <GridContainer {...styles.compareProductsContainer}>
                        <GridItem {...styles.productItems}>
                            {productIds[0] ? (
                                renderCompareProduct(0)
                            ) : (
                                <GridItem {...styles.productsBox} style={{ borderStyle: "dashed" }}></GridItem>
                            )}
                            {productIds[1] ? (
                                renderCompareProduct(1)
                            ) : (
                                <GridItem {...styles.productsBox} style={{ borderStyle: "dashed" }}></GridItem>
                            )}
                            {productIds[2] ? (
                                renderCompareProduct(2)
                            ) : (
                                <GridItem {...styles.productsBox} style={{ borderStyle: "dashed" }}></GridItem>
                            )}
                        </GridItem>
                        <GridItem {...styles.compareButtonContainer}>
                            {products.length > 1 ? (
                                <Button
                                    {...styles.headerCompareButton}
                                    onClick={handleOpenCompare}
                                    data-test-selector="productCompareOpenComparePageButton"
                                >
                                    {translate("View Comparison")}
                                </Button>
                            ) : null}
                        </GridItem>
                    </GridContainer>
                </StyledWrapper>
            ) : null}
        </Drawer>
    );
};

interface ProductCompareHeaderProps {
    styles: ProductCompareFlyOutStyles;
    productCount: number;
    onOpenCompare: () => void;
    onRemoveAll: () => void;
    toggleCompareFn: (value: string) => void;
    openCompare: string | null;
}

const ProductCompareHeader = ({
    styles,
    productCount,
    onOpenCompare,
    onRemoveAll,
    toggleCompareFn,
    openCompare,
}: ProductCompareHeaderProps) => {
    return (
        <GridContainer {...styles.headerGridContainer}>
            <GridItem {...styles.headerTitleGridItem}>
                <Typography {...styles.headerTitleText}>
                    {translate(
                        `{0} of 3 products added. Up to 3 items may be compared at a time.
`,
                        productCount.toString(),
                    )}
                </Typography>
            </GridItem>
            <GridItem {...styles.headerActionsGridItem}>
                {openCompare === "true" ? (
                    <Typography {...styles.showHideText} onClick={() => toggleCompareFn("false")}>
                        <Typography style={{ transform: "rotate(0deg)" }}>
                            <ChevronDown />
                        </Typography>
                        {"Hide"}
                    </Typography>
                ) : (
                    <Typography {...styles.showHideText} onClick={() => toggleCompareFn("true")}>
                        <Typography style={{ transform: "rotate(180deg)" }}>
                            <ChevronDown />
                        </Typography>
                        {"Show"}
                    </Typography>
                )}
            </GridItem>
        </GridContainer>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withHistory(ProductCompareFlyOut));
